import { Features } from "@tmi/types/enums/features";
import { ModelPortfolios } from "@tmi/types/enums/model-portfolios";
import { Products } from "@tmi/types/enums/products";
import { AuthorizationsByShortCode } from "@tmi/types/api/permissions";

// This type creates an alias for the raw data that is stored within IndexedDB
export type UserPermissions = {
    /* --------------------------------- LIFE-CYCLE FUNCTIONS --------------------------------- */
    initialize( categorizedPermissions: AuthorizationsByShortCode[] ): void;
    isInitialized(): boolean;
    reset(): void;

    /* ------------------------------- GENERAL-HIGH-LEVEL AUTHORIZATION ------------------------------ */
    isAuthorizedByShortCode( shortCode: string ): boolean;

    /* --------------------------------- CONTENT-BASED AUTHORIZATION --------------------------------- */
    // General Article access request - used for dynamic invocation -> this will NOT support dynamic products (by design)
    hasArticleAccessForProduct( productKey: string ): boolean;
    // General Bonus Report access request - used for dynamic invocation -> this will NOT support dynamic products (by design)
    hasBonusReportAccessForProduct( productKey: string ): boolean;
    // General Course access request - used for dynamic invocation -> this will NOT support dynamic products (by design)
    hasCourseAccessForProduct( productKey: string ): boolean;
    // General Webinar access request - used for dynamic invocation -> this will NOT support dynamic products (by design)
    hasWebinarAccessForProduct( productKey: string ): boolean;

    // Broad-stroke with "Alliance"
    isAllianceMember(): boolean;
    isFullAccessUser(): boolean;

    hasCheckPointTraderArticleAccess(): boolean;
    hasDarkMoneyIndicatorArticleAccess(): boolean;
    hasDeepMarketTraderArticleAccess(): boolean;
    hasFreemiumArticleAccess(): boolean;
    hasMonthlyTrendTraderArticleAccess(): boolean;
    hasOptionsSoupArticleAccess(): boolean;
    hasProfitSkimmerArticleAccess(): boolean;
    hasPublicArticleAccess(): boolean;
    hasSectorFocusArticleAccess(): boolean;
    hasSectorProphetsArticleAccess(): boolean;
    hasSectorProphetsEliteArticleAccess(): boolean;
    hasSectorProphetsLiteArticleAccess(): boolean;
    hasSectorProphetsProArticleAccess(): boolean;
    hasTechnicalAnalysisMillionaireArticleAccess(): boolean;
    hasTrueMarketInsidersArticleAccess(): boolean;
    hasTrueSovereignProfitsArticleAccess(): boolean;
    hasTycoonArticleAccess(): boolean;

    hasCheckPointTraderBonusReportAccess(): boolean;
    hasDarkMoneyIndicatorBonusReportAccess(): boolean;
    hasDeepMarketTraderBonusReportAccess(): boolean;
    hasFreemiumBonusReportAccess(): boolean;
    hasMonthlyTrendTraderBonusReportAccess(): boolean;
    hasOptionsSoupBonusReportAccess(): boolean;
    hasProfitSkimmerBonusReportAccess(): boolean;
    hasPublicBonusReportAccess(): boolean;
    hasSectorFocusBonusReportAccess(): boolean;
    hasSectorProphetsBonusReportAccess(): boolean;
    hasSectorProphetsEliteBonusReportAccess(): boolean;
    hasSectorProphetsLiteBonusReportAccess(): boolean;
    hasSectorProphetsProBonusReportAccess(): boolean;
    hasTechnicalAnalysisMillionaireBonusReportAccess(): boolean;
    hasTrueMarketInsidersBonusReportAccess(): boolean;
    hasTrueSovereignProfitsBonusReportAccess(): boolean;
    hasTycoonBonusReportAccess(): boolean;

    hasCheckPointTraderCourseAccess(): boolean;
    hasDarkMoneyIndicatorCourseAccess(): boolean;
    hasDeepMarketTraderCourseAccess(): boolean;
    hasFreemiumCourseAccess(): boolean;
    hasMonthlyTrendTraderCourseAccess(): boolean;
    hasOptionsSoupCourseAccess(): boolean;
    hasProfitSkimmerCourseAccess(): boolean;
    hasPublicCourseAccess(): boolean;
    hasSectorFocusCourseAccess(): boolean;
    hasSectorProphetsCourseAccess(): boolean;
    hasSectorProphetsEliteCourseAccess(): boolean;
    hasSectorProphetsLiteCourseAccess(): boolean;
    hasSectorProphetsProCourseAccess(): boolean;
    hasTechnicalAnalysisMillionaireCourseAccess(): boolean;
    hasTrueMarketInsidersCourseAccess(): boolean;
    hasTrueSovereignProfitsCourseAccess(): boolean;
    hasTycoonCourseAccess(): boolean;

    hasCheckPointTraderWebinarAccess(): boolean;
    hasDarkMoneyIndicatorWebinarAccess(): boolean;
    hasDeepMarketTraderWebinarAccess(): boolean;
    hasFreemiumWebinarAccess(): boolean;
    hasMonthlyTrendTraderWebinarAccess(): boolean;
    hasOptionsSoupWebinarAccess(): boolean;
    hasProfitSkimmerWebinarAccess(): boolean;
    hasPublicWebinarAccess(): boolean;
    hasSectorFocusWebinarAccess(): boolean;
    hasSectorProphetsWebinarAccess(): boolean;
    hasSectorProphetsEliteWebinarAccess(): boolean;
    hasSectorProphetsLiteWebinarAccess(): boolean;
    hasSectorProphetsProWebinarAccess(): boolean;
    hasTechnicalAnalysisMillionaireWebinarAccess(): boolean;
    hasTrueMarketInsidersWebinarAccess(): boolean;
    hasTrueSovereignProfitsWebinarAccess(): boolean;
    hasTycoonWebinarAccess(): boolean;

    /* --------------------------------- FEATURE-BASED AUTHORIZATION --------------------------------- */
    // General feature request - used for dynamic invocation -> this will NOT support dynamic features (by design)
    hasFeatureAccess( feature: string | Features ): boolean;

    // Specific feature permissions
    hasAllianceAccess(): boolean;
    hasArticleAccess(): boolean;
    hasBonusReportAccess(): boolean;
    hasCourseAccess(): boolean;
    hasEtfAccess(): boolean;
    hasStockAccess(): boolean;
    hasEtfListingAccess(): boolean;
    hasEquityCalculatorAccess(): boolean;
    hasMainSearchAccess(): boolean;
    hasMajorIndexBreadthAccess(): boolean;
    hasMentoringAccess(): boolean;
    hasNyseBpiAccess(): boolean;
    hasOptionsCalculatorAccess(): boolean;
    hasOptionsQuotesAccess(): boolean;
    hasPortfolioBuilderAccess(): boolean;
    hasPositionSizeCalculatorAccess(): boolean;
    hasStockReplacementStrategyCalculatorAccess(): boolean;
    hasProductTradeAlertsAccess(): boolean;
    hasProfileAccess(): boolean;
    hasRelativeStrengthMatrixAccess(): boolean;
    hasRelativeStrengthMatrixHistoryAccess(): boolean;
    hasRequestNewFeaturesAccess(): boolean;
    hasScannerAccess(): boolean;
    hasSectorAlertsAccess(): boolean;
    hasSectorAccess(): boolean;
    hasSectorBpiAccess(): boolean;
    hasSectorBreadthAccess(): boolean;
    hasSpreadCalculatorAccess(): boolean;
    hasTradeOfTheDayAccess(): boolean;
    hasWebinarAccess(): boolean;

    /* --------------------------------- MODEL-PORTFOLIO-BASED AUTHORIZATION --------------------------------- */
    // Return the minimal list of only the model portfolios to which the user currently has access:
    getModelPortfolioSlugsForAccessibleProducts(): string[];
    // General access - not really needed anymore, but here for legacy purposes - remove if not used anywhere
    hasAnyModelPortfolioAccess(): boolean;

    // General MP access request - used for dynamic invocation -> this will NOT support dynamic model portfolios (by design)
    hasModelPortfolioAccess( mpKey: string ): boolean;

    // Specific product access
    hasCheckPointTraderModelPortfolioAccess(): boolean;
    hasDeepMarketTraderModelPortfolioAccess(): boolean;
    hasMonthlyTrendTraderModelPortfolioAccess(): boolean;
    hasProfitSkimmerModelPortfolioAccess(): boolean;
    hasSectorFocusModelPortfolioAccess(): boolean;
}

export interface IPermissionsFactory {
    create( data?: AuthorizationsByShortCode[] | undefined ): UserPermissions;
}

const allianceShortCode = 'alliance';
const fullaccessShortCode = 'fullaccess';

class UserPermissionsFactory implements IPermissionsFactory {
    public create( permValues: AuthorizationsByShortCode[] ): UserPermissions {
        const permissions: UserPermissions = new Permissions();
        if ( permValues.length > 0 ) {
            permissions.initialize( permValues );
        }

        return permissions;
    }
}

class Permissions implements UserPermissions {
    public articlesPermissionsList: string[];
    public bonusReportsPermissionsList: string[];
    public coursesPermissionsList: string[];
    public featurePermissionsList: string[];
    public modelPortfolioPermissionsList: string[];
    public shortCodes: string[];
    public webinarsPermissionsList: string[];

    private _initialized: boolean;

    // External status variables
    public error: string = '';

    constructor() {
        this.error = '';

        this.articlesPermissionsList = [] as string[];
        this.bonusReportsPermissionsList = [] as string[];
        this.coursesPermissionsList = [] as string[];
        this.featurePermissionsList = [] as string[];
        this.modelPortfolioPermissionsList = [] as string[];
        this.shortCodes = [] as string[];
        this.webinarsPermissionsList = [] as string[];
        this._initialized = false;
    }

    public reset(): void {
        this.setInitialized( false );
        this.articlesPermissionsList = [];
        this.bonusReportsPermissionsList = [];
        this.coursesPermissionsList = [];
        this.featurePermissionsList = [];
        this.modelPortfolioPermissionsList = [];
        this.shortCodes = [];
        this.webinarsPermissionsList = [];
        this.error = '';
    }

    public isInitialized(): boolean {
        return this._initialized;
    }

    public setInitialized( val: boolean ): void {
        this._initialized = val;
    }

    public initialize( categorizedPermissions: AuthorizationsByShortCode[] ): void {
        try {
            if ( this.isInitialized() ) {
                return;
            }

            // Reset the current state
            this.reset();

            const uniqueFeatures: Set< string > = new Set();
            const uniqueProductArticles: Set< string > = new Set();
            const uniqueProductBonusReports: Set< string > = new Set();
            const uniqueProductCourse: Set< string > = new Set();
            const uniqueProductModelPortfolios: Set< string > = new Set();
            const uniqueProductWebinars: Set< string > = new Set();
            const uniqueShortCodes: Set< string > = new Set();

            categorizedPermissions.forEach( ( permission: AuthorizationsByShortCode ) => {
                // only add the short code once
                uniqueShortCodes.add( permission.code );

                // For each product associated with the short code, apply the permissions
                permission.products.forEach( ( item: { product: string, categories: string[] } ) => {
                    item.categories.forEach( ( category: string ) => {
                        const productSlug: string = item.product;
                        switch ( category.toLowerCase() ) {
                            case 'articles':
                                uniqueProductArticles.add( productSlug );
                                break;
                            case 'bonus-reports':
                                uniqueProductBonusReports.add( productSlug );
                                break;
                            case 'courses':
                                uniqueProductCourse.add( productSlug );
                                break;
                            case 'model-portfolios':
                                uniqueProductModelPortfolios.add( productSlug );
                                break;
                            case 'webinars':
                                uniqueProductWebinars.add( productSlug );
                                break;
                        }
                    } );
                } );

                // For each feature associated with the short code, apply the permissions
                permission.features.forEach( ( feature: string ) => uniqueFeatures.add( feature ) );
            } );

            this.articlesPermissionsList = [ ...uniqueProductArticles ];
            this.bonusReportsPermissionsList = [ ...uniqueProductBonusReports ];
            this.coursesPermissionsList = [ ...uniqueProductCourse ];
            this.featurePermissionsList = [ ...uniqueFeatures ];
            this.modelPortfolioPermissionsList = [ ...uniqueProductModelPortfolios ];
            this.shortCodes = [ ...uniqueShortCodes ];
            this.webinarsPermissionsList = [ ...uniqueProductWebinars ];

            // the final step of this method should be to set the "initialized" flag and do nothing else
            this.setInitialized( true );
        } catch ( err: any ) {
            console.warn( JSON.stringify( err.message ) );
            this.error = JSON.stringify( err );
            this.setInitialized( false );
        }
    }

    public hasFeatureAccess( feature: string | Features ): boolean {
        if ( typeof feature !== 'string' ) {
            feature = Features[ feature ].replaceAll( '_', '-' );
        }

        return this.featurePermissionsList.some( ( key: string ): boolean => key === feature );
    }

    /**
     * This function takes the minified mapping of the user's model portfolio access permissions. It is performed by doing a simply mapping over the list of all MPs. The
     * reduction step builds a string array of the MP slug values for products that have the "true" permissions set, discarding the rest, ending with a minimal list of only
     * accessible model portfolios.
     */
    public getModelPortfolioSlugsForAccessibleProducts(): string[] {
        return this.modelPortfolioPermissionsList;
    }

    public hasAllianceAccess(): boolean { return true; } //was using the dashboard permission so ... is this even needed?
    public hasArticleAccess(): boolean { return true; } //was using the dashboard permission so ... is this even needed?
    public hasBonusReportAccess(): boolean { return this.hasFeatureAccess( Features.bonus_reports ); }
    public hasCourseAccess(): boolean { return this.hasFeatureAccess( Features.courses ); }
    public hasTradeOfTheDayAccess(): boolean { return this.hasFeatureAccess( Features.trade_of_the_day ); }
    public hasEtfAccess(): boolean { return this.hasFeatureAccess( Features.etfs ); }
    public hasStockAccess(): boolean { return this.hasFeatureAccess( Features.stocks ); }
    public hasEtfListingAccess(): boolean { return this.hasFeatureAccess( Features.etf_listing ); }
    public hasEquityCalculatorAccess(): boolean { return this.hasFeatureAccess( Features.equity_calculator ); }
    public hasMainSearchAccess(): boolean { return this.hasFeatureAccess( Features.main_search ); }
    public hasMajorIndexBreadthAccess(): boolean { return this.hasFeatureAccess( Features.major_index_breadth ); }
    public hasMentoringAccess(): boolean { return this.hasFeatureAccess( Features.mentoring ); }
    public hasNyseBpiAccess(): boolean { return true; }
    public hasOptionsCalculatorAccess(): boolean { return this.hasFeatureAccess( Features.options_calculator ); }
    public hasOptionsQuotesAccess(): boolean { return this.hasFeatureAccess( Features.options_quotes ); }
    public hasPortfolioBuilderAccess(): boolean { return this.hasFeatureAccess( Features.position_key ); }
    public hasPositionSizeCalculatorAccess(): boolean { return this.hasFeatureAccess( Features.position_size_calculator ); }
    // Piggybacks on equity_calculator (Features.equity_calculator = 14). SRS
    // converts equity exposure into delta-equivalent options exposure, so it
    // belongs in the same access bundle as the Equity Calculator. If/when
    // SRS needs its own subscription gating, add a dedicated Features entry
    // and point this method at it.
    public hasStockReplacementStrategyCalculatorAccess(): boolean { return this.hasFeatureAccess( Features.equity_calculator ); }
    public hasProductTradeAlertsAccess(): boolean { return this.hasFeatureAccess( Features.trade_alerts ); }
    public hasProfileAccess(): boolean { return this.hasFeatureAccess( Features.profile ); }
    public hasRelativeStrengthMatrixAccess(): boolean { return this.hasFeatureAccess( Features.rs_matrix ); }
    public hasRelativeStrengthMatrixHistoryAccess(): boolean { return this.hasFeatureAccess( Features.rs_matrix_history ); }
    public hasRequestNewFeaturesAccess(): boolean { return this.hasFeatureAccess( Features.request_new_feature ); }
    public hasScannerAccess(): boolean { return this.hasFeatureAccess( Features.scanner ); }
    public hasSectorAlertsAccess(): boolean { return this.hasFeatureAccess( Features.alerts ); }
    public hasSectorAccess(): boolean { return this.hasFeatureAccess( Features.sectors ); }
    public hasSectorBpiAccess(): boolean { return this.hasFeatureAccess( Features.all_bpis ); }
    public hasSectorBreadthAccess(): boolean { return this.hasFeatureAccess( Features.sector_breadth ); }
    public hasSpreadCalculatorAccess(): boolean { return this.hasFeatureAccess( Features.spread_calculators ); }
    public hasWebinarAccess(): boolean { return this.hasFeatureAccess( Features.webinars ); }

    /******************************************************* MODEL PORTFOLIO ****************************************************************************/
    public hasAnyModelPortfolioAccess(): boolean {
        return this.modelPortfolioPermissionsList?.length > 0;
    }

    public hasModelPortfolioAccess( modelPortfolio: string | ModelPortfolios ): boolean {
        if ( typeof modelPortfolio !== 'string' ) {
            modelPortfolio = ModelPortfolios[ modelPortfolio ].replaceAll( '_', '-' );
        }

        return this.modelPortfolioPermissionsList.some( ( key: string ): boolean => key === modelPortfolio );
    }

    public hasCheckPointTraderModelPortfolioAccess(): boolean { return this.hasModelPortfolioAccess( ModelPortfolios.checkpoint_trader ); }
    public hasDeepMarketTraderModelPortfolioAccess(): boolean { return this.hasModelPortfolioAccess( ModelPortfolios.deep_market_trader ); }
    public hasMonthlyTrendTraderModelPortfolioAccess(): boolean { return this.hasModelPortfolioAccess( ModelPortfolios.monthly_trend_trader ); }
    public hasProfitSkimmerModelPortfolioAccess(): boolean { return this.hasModelPortfolioAccess( ModelPortfolios.profit_skimmer ); }
    public hasSectorFocusModelPortfolioAccess(): boolean { return this.hasModelPortfolioAccess( ModelPortfolios.sector_focus ); }

    public isAuthorizedByShortCode( codeHash: string ): boolean {
        // this creates an array of short codes (e.g.; const authorizationCodes = [ 'psk', 'fullaccess', 'alliance' ];)
        const authorizationCodes: string[] = codeHash.toLowerCase().split( ',' ).map( ( code ) => code.trim() );

        // Map the short codes to the lower-cased values
        const normalizedCodes: string[] = this.shortCodes.map( ( code: string ) => code.toLowerCase() );
        // This iterates over the first item of each sub-array in the deconstructedCodes and tests to see if there is a matching short code assigned to the current user
        return normalizedCodes?.some( ( key: string ) => authorizationCodes.includes( key ) ) ?? false;
    }

    public isAllianceMember(): boolean {
        return this.shortCodes.some( ( code: string ) => allianceShortCode === code.toLowerCase() );
    }

    public isFullAccessUser(): boolean {
        return this.shortCodes.some( ( code: string ) => fullaccessShortCode === code.toLowerCase() );
    }

    /******************************************************* CONTENTFUL ****************************************************************************/
    public hasArticleAccessForProduct( product: string | Products ): boolean {
        if ( typeof product !== 'string' ) {
            product = Products[ product ].replaceAll( '_', '-' );
        }

        return this.articlesPermissionsList.some( ( key: string ): boolean => key === product );
    }

    public hasBonusReportAccessForProduct( product: string | Products ): boolean {
        if ( typeof product !== 'string' ) {
            product = Products[ product ].replaceAll( '_', '-' );
        }

        return this.bonusReportsPermissionsList.some( ( key: string ): boolean => key === product );
    }

    public hasCourseAccessForProduct( product: string | Products ): boolean {
        if ( typeof product !== 'string' ) {
            product = Products[ product ].replaceAll( '_', '-' );
        }

        return this.coursesPermissionsList.some( ( key: string ): boolean => key === product );
    }

    public hasWebinarAccessForProduct( product: string | Products ): boolean {
        if ( typeof product !== 'string' ) {
            product = Products[ product ].replaceAll( '_', '-' );
        }

        return this.webinarsPermissionsList.some( ( key: string ): boolean => key === product );
    }

    /* ---------------------------------------------------------------------------------- */
    //                                  ARTICLES
    /* ---------------------------------------------------------------------------------- */
    public hasCheckPointTraderArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.checkpoint_trader );
    }

    public hasDarkMoneyIndicatorArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.dark_money_indicator );
    }

    public hasDeepMarketTraderArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.deep_market_trader );
    }

    public hasFreemiumArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.freemium );
    }

    public hasMonthlyTrendTraderArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.monthly_trend_trader );
    }

    public hasOptionsSoupArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.options_soup );
    }

    public hasProfitSkimmerArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.profit_skimmer );
    }

    public hasPublicArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.public );
    }

    public hasSectorFocusArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.sector_focus );
    }

    public hasSectorProphetsArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.sector_prophets );
    }

    public hasSectorProphetsEliteArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.sector_prophets_elite );
    }

    public hasSectorProphetsLiteArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.sector_prophets_lite );
    }

    public hasSectorProphetsProArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.sector_prophets_pro );
    }

    public hasTechnicalAnalysisMillionaireArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.technical_analysis_millionaire );
    }

    public hasTrueMarketInsidersArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.true_market_insiders );
    }

    public hasTrueSovereignProfitsArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.true_sovereign_profits );
    }

    public hasTycoonArticleAccess(): boolean {
        return this.hasArticleAccessForProduct( Products.tycoon );
    }

    /* ---------------------------------------------------------------------------------- */
    //                          BONUS REPORTS
    /* ---------------------------------------------------------------------------------- */
    public hasCheckPointTraderBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.checkpoint_trader );
    }

    public hasDarkMoneyIndicatorBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.dark_money_indicator );
    }

    public hasDeepMarketTraderBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.deep_market_trader );
    }

    public hasFreemiumBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.freemium );
    }

    public hasMonthlyTrendTraderBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.monthly_trend_trader );
    }

    public hasOptionsSoupBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.options_soup );
    }

    public hasProfitSkimmerBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.profit_skimmer );
    }

    public hasPublicBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.public );
    }

    public hasSectorFocusBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.sector_focus );
    }

    public hasSectorProphetsBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.sector_prophets );
    }

    public hasSectorProphetsEliteBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.sector_prophets_elite );
    }

    public hasSectorProphetsLiteBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.sector_prophets_lite );
    }

    public hasSectorProphetsProBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.sector_prophets_pro );
    }

    public hasTechnicalAnalysisMillionaireBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.technical_analysis_millionaire );
    }

    public hasTrueMarketInsidersBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.true_market_insiders );
    }

    public hasTrueSovereignProfitsBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.true_sovereign_profits );
    }

    public hasTycoonBonusReportAccess(): boolean {
        return this.hasBonusReportAccessForProduct( Products.tycoon );
    }

    /* ---------------------------------------------------------------------------------- */
    //                                  COURSES
    /* ---------------------------------------------------------------------------------- */
    public hasCheckPointTraderCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.checkpoint_trader );
    }

    public hasDarkMoneyIndicatorCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.dark_money_indicator );
    }

    public hasDeepMarketTraderCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.deep_market_trader );
    }

    public hasFreemiumCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.freemium );
    }

    public hasMonthlyTrendTraderCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.monthly_trend_trader );
    }

    public hasOptionsSoupCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.options_soup );
    }

    public hasProfitSkimmerCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.profit_skimmer );
    }

    public hasPublicCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.public );
    }

    public hasSectorFocusCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.sector_focus );
    }

    public hasSectorProphetsCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.sector_prophets );
    }

    public hasSectorProphetsEliteCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.sector_prophets_elite );
    }

    public hasSectorProphetsLiteCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.sector_prophets_lite );
    }

    public hasSectorProphetsProCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.sector_prophets_pro );
    }

    public hasTechnicalAnalysisMillionaireCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.technical_analysis_millionaire );
    }

    public hasTrueMarketInsidersCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.true_market_insiders );
    }

    public hasTrueSovereignProfitsCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.true_sovereign_profits );
    }

    public hasTycoonCourseAccess(): boolean {
        return this.hasCourseAccessForProduct( Products.tycoon );
    }

    /* ---------------------------------------------------------------------------------- */
    //                                  WEBINARS
    /* ---------------------------------------------------------------------------------- */
    public hasCheckPointTraderWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.checkpoint_trader );
    }

    public hasDarkMoneyIndicatorWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.dark_money_indicator );
    }

    public hasDeepMarketTraderWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.deep_market_trader );
    }

    public hasFreemiumWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.freemium );
    }

    public hasMonthlyTrendTraderWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.monthly_trend_trader );
    }

    public hasOptionsSoupWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.options_soup );
    }

    public hasProfitSkimmerWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.profit_skimmer );
    }

    public hasPublicWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.public );
    }

    public hasSectorFocusWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.sector_focus );
    }

    public hasSectorProphetsWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.sector_prophets );
    }

    public hasSectorProphetsEliteWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.sector_prophets_elite );
    }

    public hasSectorProphetsLiteWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.sector_prophets_lite );
    }

    public hasSectorProphetsProWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.sector_prophets_pro );
    }

    public hasTechnicalAnalysisMillionaireWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.technical_analysis_millionaire );
    }

    public hasTrueMarketInsidersWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.true_market_insiders );
    }

    public hasTrueSovereignProfitsWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.true_sovereign_profits );
    }

    public hasTycoonWebinarAccess(): boolean {
        return this.hasWebinarAccessForProduct( Products.tycoon );
    }
}

const userPermissionsFactory: IPermissionsFactory = new UserPermissionsFactory();
export default userPermissionsFactory;
